import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import { useOwnerQa } from "../contexts/OwnerQaContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  TRAINING_RATING_HOUSES,
  buildTrainingHouseQuery,
  findTrainingHouseById,
  findTrainingHouseByRating,
  formatTrainingHouseRange,
  normalizeTrainingRating,
} from "../config/trainingMap";

const COPY = {
  title: {
    en: "Training Map",
    pl: "Mapa treningu",
    es: "Mapa de entrenamiento",
  },
  subtitle: {
    en: "Enter Chesshood training through a house that matches your rating.",
    pl: "Wejdz do treningu Chesshood przez domek dopasowany do Twojego rankingu.",
    es: "Entra al entrenamiento de Chesshood por la casa que encaja con tu nivel.",
  },
  currentRating: {
    en: "Current rating",
    pl: "Aktualny ranking",
    es: "Rating actual",
  },
  recommended: {
    en: "Recommended house",
    pl: "Polecany domek",
    es: "Casa recomendada",
  },
  chooseHouse: {
    en: "Choose a house to start puzzle training in that rating band.",
    pl: "Wybierz domek, aby zaczac trening puzzli w tym przedziale rankingowym.",
    es: "Elige una casa para empezar el entrenamiento de puzzles en ese rango.",
  },
  entering: {
    en: "Entering",
    pl: "Wchodze do",
    es: "Entrando en",
  },
  lockedHouse: {
    en: "Locked until rating",
    pl: "Zamkniete do rankingu",
    es: "Bloqueado hasta rating",
  },
  enterHouse: {
    en: "Enter training",
    pl: "Wejdz na trening",
    es: "Entrar al entrenamiento",
  },
  home: {
    en: "Home",
    pl: "Start",
    es: "Inicio",
  },
  longGame: {
    en: "Long Game · 7 days free",
    pl: "Long Game · 7 dni za darmo",
    es: "Partida larga · 7 días gratis",
  },
  login: {
    en: "Log in",
    pl: "Zaloguj sie",
    es: "Iniciar sesion",
  },
  register: {
    en: "Register",
    pl: "Zarejestruj sie",
    es: "Registrate",
  },
  authTitleLoggedIn: {
    en: "You are logged in",
    pl: "Jestes zalogowany",
    es: "Has iniciado sesion",
  },
  authTitleGuest: {
    en: "You are in guest mode",
    pl: "Jestes w trybie goscia",
    es: "Estas en modo invitado",
  },
  authLoggedInCopy: {
    en: "Your rating houses and progress can move with your account.",
    pl: "Twoje domki rankingowe i postep moga byc zapisane na koncie.",
    es: "Tus casas por rating y tu progreso pueden guardarse en tu cuenta.",
  },
  authGuestCopy: {
    en: "Sign in or register to train with 7 free puzzles every day and save your rating and map progress.",
    pl: "Zaloguj się lub zarejestruj, aby codziennie rozwiązywać 7 darmowych puzzli oraz zapisywać ranking i postępy na mapie.",
    es: "Inicia sesión o regístrate para resolver 7 puzzles gratis cada día y guardar tu rating y tu progreso en el mapa.",
  },
  signedInAs: {
    en: "Signed in as",
    pl: "Zalogowany jako",
    es: "Sesion iniciada como",
  },
  guestBadge: {
    en: "Guest",
    pl: "Gosc",
    es: "Invitado",
  },
  loginPrompt: {
    en: "Sign in to start your 7 free daily puzzles. No subscription is needed.",
    pl: "Zaloguj się, aby rozpocząć 7 darmowych puzzli dziennie. Abonament nie jest wymagany.",
    es: "Inicia sesión para empezar tus 7 puzzles gratis al día. No necesitas una suscripción.",
  },
  loading: {
    en: "Loading your training map...",
    pl: "Laduje mape treningu...",
    es: "Cargando tu mapa de entrenamiento...",
  },
  promotion: {
    en: "Your rating moved up. Go to the highlighted house for the next training band.",
    pl: "Twoj ranking wzrosl. Przejdz do podswietlonego domku po kolejny przedzial treningowy.",
    es: "Tu rating subio. Ve a la casa resaltada para el siguiente rango.",
  },
  demotion: {
    en: "Too bad. Let's go a little lower and rebuild there.",
    pl: "Szkoda. Musimy troche zejsc i odbudowac ranking tam.",
    es: "Que pena. Bajemos un poco y reconstruyamos alli.",
  },
  hoodUp: {
    en: "Bravo. You moved higher. This is your new house now.",
    pl: "Brawo, wszedles wyzej. To jest teraz Twoj nowy domek.",
    es: "Bravo. Subiste mas arriba. Esta es ahora tu nueva casa.",
  },
  hoodDown: {
    en: "Sorry. We need to go a little lower and rebuild there.",
    pl: "Przykro mi, ale idziemy nizej i tam odbudujemy ranking.",
    es: "Lo siento. Tenemos que bajar un poco y reconstruir alli.",
  },
  hoodDefault: {
    en: "Choose the house and let's train.",
    pl: "Wybierz domek i trenujemy.",
    es: "Elige la casa y entrenamos.",
  },
  hoodBadge: {
    en: "Hood guide",
    pl: "Hood przewodnik",
    es: "Guia Hood",
  },
};

const START_HOOD_POSE = {
  x: 45,
  y: 88,
  scale: 0.92,
  opacity: 1,
};

const WALK_DURATION_MS = 950;
const ENTER_DURATION_MS = 430;
const SHIFT_HOOD_MESSAGE_DURATION_MS = 39000;
const PAWN_VISUAL_CENTER_X = 50.5;
const PAWN_VISUAL_CENTER_Y = 48.9;
const PAWN_MIN_WIDTH_PX = 110;
const PAWN_MAX_WIDTH_PX = 180;
const PAWN_VIEWPORT_WIDTH_RATIO = 0.18;
const PAWN_ASPECT_RATIO = 1536 / 1024;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getPawnBaseWidth = (viewportWidth) =>
  clamp(
    viewportWidth * PAWN_VIEWPORT_WIDTH_RATIO,
    PAWN_MIN_WIDTH_PX,
    PAWN_MAX_WIDTH_PX,
  );

const createWalkPose = ({ clickX, clickY, sceneRect, viewportWidth }) => {
  const normalizedY = clamp(clickY, 0, 100);
  const scale = 0.5 + (normalizedY / 100) * 0.38;
  const pawnWidthPx = getPawnBaseWidth(viewportWidth) * scale;
  const pawnHeightPx = pawnWidthPx * PAWN_ASPECT_RATIO;
  const offsetXPct =
    (((PAWN_VISUAL_CENTER_X - 50) / 100) * pawnWidthPx * 100) / sceneRect.width;
  const offsetYPct =
    (((100 - PAWN_VISUAL_CENTER_Y) / 100) * pawnHeightPx * 100) /
    sceneRect.height;

  return {
    x: clickX - offsetXPct,
    y: clickY + offsetYPct,
    scale,
    opacity: 1,
  };
};

const createHousePose = (house, sceneRect, viewportWidth) => {
  const clickX = house.position.left + house.position.width / 2;
  const clickY = Math.min(96, house.position.top + house.position.height * 0.94);

  return createWalkPose({
    clickX,
    clickY,
    sceneRect,
    viewportWidth,
  });
};

function resolveDisplayedRating(user, queryRating) {
  const ratingCandidates = [
    queryRating,
    user?.rating,
    user?.puzzle_rating,
    user?.puzzleRating,
    user?.ratings?.puzzle,
    user?.stats?.rating,
    100,
  ];

  for (const candidate of ratingCandidates) {
    const normalized = normalizeTrainingRating(candidate);
    if (normalized !== null && normalized > 0) {
      return normalized;
    }
  }

  return 0;
}

export default function EntryTraining() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pick } = useLanguage();
  const { user, firebaseUser, loading } = useContext(UserContext);
  const qa = useOwnerQa();
  const sceneRef = useRef(null);
  const timersRef = useRef([]);
  const [hoodPose, setHoodPose] = useState(START_HOOD_POSE);
  const [activeHouseId, setActiveHouseId] = useState(null);
  const [guidedHouseId, setGuidedHouseId] = useState(null);
  const [showShiftGuideMessage, setShowShiftGuideMessage] = useState(false);
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const queryRating = normalizeTrainingRating(searchParams.get("rating"));
  // Owner QA's simulated Elo must match the value shown on the puzzle board.
  // This affects only the preview map; the real saved rating is unchanged.
  const currentRating = qa.active
    ? (normalizeTrainingRating(qa.config.rating) ?? resolveDisplayedRating(user, queryRating))
    : resolveDisplayedRating(user, queryRating);
  const recommendedHouse = findTrainingHouseByRating(currentRating);
  const previousHouse = findTrainingHouseById(searchParams.get("fromHouse"));
  const activeHouse =
    TRAINING_RATING_HOUSES.find((house) => house.id === activeHouseId) || null;
  const shiftDirection = searchParams.get("shift") || "";
  const showShiftBanner =
    Boolean(queryRating !== null) &&
    Boolean(previousHouse) &&
    previousHouse?.id !== recommendedHouse?.id &&
    (shiftDirection === "up" || shiftDirection === "down");
  const hoodCopy =
    activeHouse
      ? `${pick(COPY.entering)} ${formatTrainingHouseRange(activeHouse)}...`
      : showShiftGuideMessage && shiftDirection === "up"
      ? pick(COPY.hoodUp)
      : showShiftGuideMessage && shiftDirection === "down"
        ? pick(COPY.hoodDown)
        : pick(COPY.hoodDefault);
  const displayName =
    user?.fullName ||
    user?.full_name ||
    user?.name ||
    user?.username ||
    user?.nick ||
    firebaseUser?.displayName ||
    firebaseUser?.email ||
    "Chesshood player";

  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (
      !showShiftBanner ||
      !sceneRef.current ||
      !previousHouse ||
      !recommendedHouse
    ) {
      setShowShiftGuideMessage(false);
      setGuidedHouseId(null);
      return;
    }

    const sceneRect = sceneRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const startingPose = createHousePose(previousHouse, sceneRect, viewportWidth);
    const destinationPose = createHousePose(
      recommendedHouse,
      sceneRect,
      viewportWidth,
    );

    clearTimers();
    setShowShiftGuideMessage(true);
    setGuidedHouseId(recommendedHouse.id);
    setHoodPose(startingPose);

    timersRef.current.push(
      setTimeout(() => {
        setHoodPose(destinationPose);
      }, 120),
    );

    timersRef.current.push(
      setTimeout(() => {
        setShowShiftGuideMessage(false);
        setGuidedHouseId(null);
      }, SHIFT_HOOD_MESSAGE_DURATION_MS),
    );
  }, [previousHouse, recommendedHouse, showShiftBanner]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const startHouseJourney = (house, destinationRoute) => {
    if (activeHouseId || !sceneRef.current) {
      return;
    }

    const sceneRect = sceneRef.current.getBoundingClientRect();
    const targetX = house.position.left + house.position.width / 2;
    const targetY = Math.min(96, house.position.top + house.position.height * 0.94);
    const walkPose = createWalkPose({
      clickX: targetX,
      clickY: targetY,
      sceneRect,
      viewportWidth: window.innerWidth,
    });
    const enterPose = {
      ...walkPose,
      y: walkPose.y - 4,
      scale: Math.max(0.18, walkPose.scale * 0.24),
      opacity: 0,
    };

    clearTimers();
    setActiveHouseId(house.id);
    setHoodPose(walkPose);

    timersRef.current.push(
      setTimeout(() => {
        setHoodPose(enterPose);
      }, WALK_DURATION_MS),
    );

    timersRef.current.push(
      setTimeout(() => {
        navigate(destinationRoute);
      }, WALK_DURATION_MS + ENTER_DURATION_MS),
    );
  };

  const handleEnterHouse = (house) => {
    if (showShiftGuideMessage) {
      setShowShiftGuideMessage(false);
      setGuidedHouseId(null);
    }

    // The training route signs guests in and retains their chosen house.
    const destinationRoute = `/play?${buildTrainingHouseQuery(house)}`;

    if (currentRating < house.ratingFrom) {
      return;
    }

    startHouseJourney(house, destinationRoute);
  };

  const handleSceneClick = (event) => {
    if (activeHouseId) {
      return;
    }

    if (showShiftGuideMessage) {
      setShowShiftGuideMessage(false);
      setGuidedHouseId(null);
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    clearTimers();
    setHoodPose(
      createWalkPose({
        clickX: x,
        clickY: y,
        sceneRect: rect,
        viewportWidth: window.innerWidth,
      }),
    );
  };

  return (
    <div className="ch-entry-training-screen">
      <style>{`
        .ch-entry-training-screen {
          min-height: 100vh;
          min-height: 100dvh;
          padding: 14px;
          background:
            linear-gradient(180deg, rgba(7, 21, 37, 0.28), rgba(8, 18, 34, 0.82)),
            linear-gradient(135deg, #0b2238, #102844 42%, #1e3d61);
          color: #fff6df;
        }

        .ch-entry-training-shell {
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
          gap: 18px;
          align-items: start;
        }

        .ch-entry-training-panel,
        .ch-entry-training-map-shell {
          border: 1px solid rgba(255, 236, 185, 0.18);
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 22px 46px rgba(0, 0, 0, 0.28);
        }

        .ch-entry-training-panel {
          padding: 22px 20px;
          background:
            linear-gradient(180deg, rgba(18, 39, 59, 0.96), rgba(8, 21, 35, 0.96));
          display: grid;
          gap: 14px;
        }

        .ch-entry-training-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .ch-entry-training-link,
        .ch-entry-training-button {
          border: 0;
          border-radius: 16px;
          padding: 12px 16px;
          font-weight: 800;
          text-decoration: none;
          cursor: pointer;
          transition: transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease;
        }

        .ch-entry-training-link:hover,
        .ch-entry-training-button:hover {
          transform: translateY(-1px);
        }

        .ch-entry-training-link {
          color: #fff1c2;
          background: rgba(255, 223, 143, 0.12);
          border: 1px solid rgba(255, 223, 143, 0.34);
        }

        .ch-entry-training-button {
          color: #12311f;
          background: linear-gradient(135deg, #f0d45d, #ffef9e);
          box-shadow: 0 12px 24px rgba(240, 212, 93, 0.22);
        }

        .ch-entry-training-title {
          margin: 0;
          font-size: clamp(2rem, 4vw, 2.8rem);
          line-height: 1.02;
          letter-spacing: 0.02em;
        }

        .ch-entry-training-subtitle,
        .ch-entry-training-copy,
        .ch-entry-training-login {
          margin: 0;
          color: rgba(255, 242, 213, 0.9);
          line-height: 1.6;
        }

        .ch-entry-training-stat-grid {
          display: grid;
          gap: 12px;
        }

        .ch-entry-training-auth-card {
          padding: 16px;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 240, 194, 0.14);
          display: grid;
          gap: 10px;
        }

        .ch-entry-training-auth-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
        }

        .ch-entry-training-auth-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 900;
          color: #fff0be;
        }

        .ch-entry-training-auth-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 32px;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .ch-entry-training-auth-badge-logged {
          background: rgba(103, 232, 174, 0.14);
          border: 1px solid rgba(103, 232, 174, 0.32);
          color: #baf7d4;
        }

        .ch-entry-training-auth-badge-guest {
          background: rgba(255, 213, 116, 0.14);
          border: 1px solid rgba(255, 213, 116, 0.28);
          color: #ffe5a1;
        }

        .ch-entry-training-auth-name {
          margin: 0;
          color: #ffffff;
          font-size: 0.94rem;
          font-weight: 800;
          line-height: 1.5;
          word-break: break-word;
        }

        .ch-entry-training-auth-copy {
          margin: 0;
          color: rgba(255, 242, 213, 0.88);
          line-height: 1.6;
        }

        .ch-entry-training-auth-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .ch-entry-training-stat {
          padding: 14px 16px;
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 240, 194, 0.12);
        }

        .ch-entry-training-stat-label {
          display: block;
          font-size: 0.78rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: rgba(255, 234, 173, 0.72);
          margin-bottom: 6px;
        }

        .ch-entry-training-stat-value {
          display: block;
          font-size: 1.35rem;
          font-weight: 900;
          color: #ffe69a;
        }

        .ch-entry-training-promotion {
          padding: 14px 16px;
          border-radius: 18px;
          background: rgba(67, 165, 112, 0.16);
          border: 1px solid rgba(142, 241, 171, 0.34);
          color: #efffe7;
          font-weight: 700;
        }

        .ch-entry-training-map-shell {
          position: relative;
          background:
            linear-gradient(180deg, rgba(11, 18, 31, 0.08), rgba(11, 18, 31, 0.34));
        }

        .ch-entry-training-map {
          position: relative;
          width: 100%;
          aspect-ratio: 2 / 3;
          background-image:
            linear-gradient(180deg, rgba(8, 14, 24, 0.06), rgba(8, 14, 24, 0.24)),
            url('/gamefication/tlo.png');
          background-size: cover;
          background-position: center;
          isolation: isolate;
        }

        .ch-entry-training-map-rating {
          position: absolute;
          top: 18px;
          right: 18px;
          z-index: 4;
          min-width: 128px;
          padding: 10px 14px;
          border-radius: 18px;
          background: rgba(8, 18, 33, 0.82);
          border: 1px solid rgba(255, 228, 123, 0.34);
          box-shadow: 0 14px 28px rgba(0, 0, 0, 0.24);
          backdrop-filter: blur(6px);
          text-align: right;
        }

        .ch-entry-training-map-rating-label {
          display: block;
          margin-bottom: 4px;
          color: rgba(255, 232, 169, 0.82);
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .ch-entry-training-map-rating-value {
          display: block;
          color: #fff3bd;
          font-size: 1.4rem;
          font-weight: 900;
          line-height: 1;
        }

        .ch-entry-training-house {
          position: absolute;
          display: grid;
          place-items: center;
          padding: 10px;
          border-radius: 18px;
          border: 0;
          background: transparent;
          color: #fff6e0;
          cursor: pointer;
          transition:
            transform 180ms ease,
            opacity 180ms ease;
          z-index: 2;
        }

        .ch-entry-training-house:hover {
          transform: scale(1.03);
          opacity: 0.88;
        }

        .ch-entry-training-house:disabled {
          cursor: not-allowed;
          transform: none;
          opacity: 0.42;
        }

        .ch-entry-training-house:disabled:hover {
          transform: none;
          opacity: 0.42;
        }

        .ch-entry-training-house-recommended {
          opacity: 1;
        }

        .ch-entry-training-house-active {
          opacity: 1;
        }

        .ch-entry-training-house-locked .ch-entry-training-house-label {
          color: rgba(255, 244, 214, 0.78);
        }

        .ch-entry-training-house-label {
          display: block;
          font-size: clamp(0.84rem, 1.55vw, 1rem);
          font-weight: 900;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.34);
        }

        .ch-entry-training-house-pill {
          margin-top: 6px;
          padding: 4px 8px;
          border-radius: 999px;
          background: rgba(255, 228, 123, 0.92);
          color: #4c3100;
          font-size: 0.68rem;
          font-weight: 900;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .ch-entry-training-hood {
          position: absolute;
          left: 0;
          top: 0;
          width: clamp(110px, 18vw, 180px);
          display: grid;
          justify-items: center;
          gap: 8px;
          pointer-events: none;
          z-index: 3;
          transition:
            left ${WALK_DURATION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1),
            top ${WALK_DURATION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1),
            transform ${WALK_DURATION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1),
            opacity ${ENTER_DURATION_MS}ms ease;
        }

        .ch-entry-training-hood-badge {
          padding: 5px 9px;
          border-radius: 999px;
          background: rgba(255, 227, 136, 0.94);
          color: #5b3900;
          font-size: 0.64rem;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          box-shadow: 0 10px 18px rgba(42, 25, 0, 0.18);
        }

        .ch-entry-training-hood-image {
          width: 100%;
          max-width: 120px;
          filter: drop-shadow(0 12px 18px rgba(0, 0, 0, 0.28));
        }

        .ch-entry-training-hood-bubble {
          max-width: 220px;
          padding: 12px 14px;
          border-radius: 18px;
          background: rgba(255, 252, 241, 0.96);
          color: #243041;
          font-size: 0.82rem;
          font-weight: 800;
          line-height: 1.45;
          text-align: center;
          box-shadow: 0 16px 30px rgba(10, 16, 24, 0.2);
        }

        .ch-entry-training-loading {
          display: grid;
          place-items: center;
          min-height: 100vh;
          min-height: 100dvh;
          color: #fff1c2;
          background: linear-gradient(180deg, #102842, #0a1728);
          font-size: 1.1rem;
          font-weight: 800;
        }

        @media (max-width: 980px) {
          .ch-entry-training-shell {
            grid-template-columns: 1fr;
          }

          .ch-entry-training-panel {
            order: 2;
          }

          .ch-entry-training-map-shell {
            order: 1;
          }
        }

        @media (max-width: 640px) {
          .ch-entry-training-screen {
            padding: 10px;
          }

          .ch-entry-training-panel {
            padding: 18px 16px;
            border-radius: 22px;
          }

          .ch-entry-training-map-shell {
            border-radius: 22px;
          }

          .ch-entry-training-house {
            padding: 8px;
            border-radius: 14px;
          }

          .ch-entry-training-house-label {
            font-size: 0.8rem;
          }

          .ch-entry-training-house-pill {
            font-size: 0.62rem;
            padding: 3px 7px;
          }

          .ch-entry-training-map-rating {
            top: 12px;
            right: 12px;
            min-width: 110px;
            padding: 9px 12px;
            border-radius: 16px;
          }

          .ch-entry-training-map-rating-value {
            font-size: 1.2rem;
          }
        }
      `}</style>

      {loading ? (
        <div className="ch-entry-training-loading">{pick(COPY.loading)}</div>
      ) : (
        <div className="ch-entry-training-shell">
          <aside className="ch-entry-training-panel">
            <div className="ch-entry-training-actions">
              <Link to="/" className="ch-entry-training-link">
                {pick(COPY.home)}
              </Link>
              {firebaseUser && (
                <Link to="/training/long-game" className="ch-entry-training-button">
                  {pick(COPY.longGame)}
                </Link>
              )}
              {!firebaseUser && (
                <Link to="/login" className="ch-entry-training-button">
                  {pick(COPY.login)}
                </Link>
              )}
            </div>

            <div>
              <h1 className="ch-entry-training-title">{pick(COPY.title)}</h1>
              <p className="ch-entry-training-subtitle">{pick(COPY.subtitle)}</p>
            </div>

            <div className="ch-entry-training-auth-card">
              <div className="ch-entry-training-auth-top">
                <h2 className="ch-entry-training-auth-title">
                  {firebaseUser
                    ? pick(COPY.authTitleLoggedIn)
                    : pick(COPY.authTitleGuest)}
                </h2>
                <span
                  className={`ch-entry-training-auth-badge ${
                    firebaseUser
                      ? "ch-entry-training-auth-badge-logged"
                      : "ch-entry-training-auth-badge-guest"
                  }`}
                >
                  {firebaseUser ? pick(COPY.authTitleLoggedIn) : pick(COPY.guestBadge)}
                </span>
              </div>

              <p className="ch-entry-training-auth-name">
                {firebaseUser
                  ? `${pick(COPY.signedInAs)}: ${displayName}`
                  : `${pick(COPY.guestBadge)} mode`}
              </p>

              <p className="ch-entry-training-auth-copy">
                {firebaseUser
                  ? pick(COPY.authLoggedInCopy)
                  : pick(COPY.authGuestCopy)}
              </p>

              {!firebaseUser && (
                <div className="ch-entry-training-auth-actions">
                  <Link to="/login" className="ch-entry-training-link">
                    {pick(COPY.login)}
                  </Link>
                  <Link to="/register" className="ch-entry-training-button">
                    {pick(COPY.register)}
                  </Link>
                </div>
              )}
            </div>

            <div className="ch-entry-training-stat-grid">
              <div className="ch-entry-training-stat">
                <span className="ch-entry-training-stat-label">
                  {pick(COPY.currentRating)}
                </span>
                <span className="ch-entry-training-stat-value">{currentRating}</span>
              </div>
              <div className="ch-entry-training-stat">
                <span className="ch-entry-training-stat-label">
                  {pick(COPY.recommended)}
                </span>
                <span className="ch-entry-training-stat-value">
                  {formatTrainingHouseRange(recommendedHouse)}
                </span>
              </div>
            </div>

            {showShiftBanner && (
              <div className="ch-entry-training-promotion">
                {shiftDirection === "down"
                  ? pick(COPY.demotion)
                  : pick(COPY.promotion)}
              </div>
            )}

            <p className="ch-entry-training-copy">{pick(COPY.chooseHouse)}</p>

            {!firebaseUser && (
              <p className="ch-entry-training-login">{pick(COPY.loginPrompt)}</p>
            )}
          </aside>

          <section className="ch-entry-training-map-shell">
            <div
              ref={sceneRef}
              className="ch-entry-training-map"
              onClick={handleSceneClick}
            >
              <div className="ch-entry-training-map-rating">
                <span className="ch-entry-training-map-rating-label">
                  {pick(COPY.currentRating)}
                </span>
                <span className="ch-entry-training-map-rating-value">
                  {currentRating}
                </span>
              </div>

              <div
                className="ch-entry-training-hood"
                aria-hidden="true"
                style={{
                  left: `${hoodPose.x}%`,
                  top: `${hoodPose.y}%`,
                  opacity: hoodPose.opacity,
                  transform: `translate(-50%, -100%) scale(${hoodPose.scale})`,
                }}
              >
                <div className="ch-entry-training-hood-badge">
                  {pick(COPY.hoodBadge)}
                </div>
                <div className="ch-entry-training-hood-bubble">{hoodCopy}</div>
                <img
                  src="/hood.png"
                  alt=""
                  className="ch-entry-training-hood-image"
                />
              </div>

              {TRAINING_RATING_HOUSES.map((house) => {
                const isRecommended = house.id === recommendedHouse?.id;
                const isLocked = currentRating < house.ratingFrom;
                const houseTitle = isLocked
                  ? `${pick(COPY.lockedHouse)} ${house.ratingFrom}`
                  : `${pick(COPY.enterHouse)} ${house.label}`;

                return (
                  <button
                    key={house.id}
                    type="button"
                    className={`ch-entry-training-house ${isRecommended ? "ch-entry-training-house-recommended" : ""} ${isLocked ? "ch-entry-training-house-locked" : ""} ${activeHouseId === house.id || guidedHouseId === house.id ? "ch-entry-training-house-active" : ""}`}
                    style={{
                      left: `${house.position.left}%`,
                      top: `${house.position.top}%`,
                      width: `${house.position.width}%`,
                      height: `${house.position.height}%`,
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleEnterHouse(house);
                    }}
                    aria-label={houseTitle}
                    title={houseTitle}
                    disabled={isLocked || Boolean(activeHouseId)}
                  >
                    <span className="ch-entry-training-house-label">{house.label}</span>
                    {isLocked ? (
                      <span className="ch-entry-training-house-pill">
                        {pick(COPY.lockedHouse)} {house.ratingFrom}
                      </span>
                    ) : isRecommended ? (
                      <span className="ch-entry-training-house-pill">
                        {pick(COPY.recommended)}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

